// What the town needs to burn down (sim/destruct.ts): every triangle of the town mesh is tagged
// with the building it belongs to, the mesh is reordered so each building is one contiguous run
// of indices, and a rubble pile for each building is baked in behind it, hidden. A collapse is then
// a few index writes (the building's run degenerates, its rubble's run comes back), shared by the
// atlas mesh, its ink hull and the shadow pass, with no rebuild and no allocation mid-fight.
//
// The per-vertex `bld` attribute carries the building (index + 1; negative on its rubble, 0 for
// the street and everything else) so the atlas shader can char it, glow it and crumple it down.

import type { World, Box } from '../../../sim/world';
import { hash32 } from '../../../sim/core';
import { BUILDING } from '../../../sim/destruct';
import { Jit } from './geo';
import { KitGeo, RGB } from './town_kit';
import { TILES, TileName } from './town_atlas';
import { beam, box, Frame, P3 } from './town_frame';

/** Index runs (start, count, in indices) for one building in one mesh. */
export interface Run { start: number; count: number }
export interface RuinLayout {
  /** per building: its body in the solid mesh, its body in the cut mesh, its rubble (solid mesh) */
  solid: Run[]; cut: Run[]; rubble: Run[];
  /** per-vertex building tag, matching the reordered geometry */
  solidBld: Float32Array; cutBld: Float32Array;
  /** sequential index arrays (with the rubble runs zeroed out) */
  solidIdx: Uint32Array; cutIdx: Uint32Array;
  /** the original rubble indices, to restore on collapse */
  rubbleIdx: Uint32Array[];
}

const WALL_TILES: TileName[] = ['clap', 'clap2', 'batten'];

/** Which building a triangle belongs to, by where it is (pieces added after the building pass). */
function claim(buildings: Box[], ty: number, ax: number, ay: number, az: number, bx: number, by: number, bz: number, cx: number, cy: number, cz: number) {
  const mx = (ax + bx + cx) / 3, my = (ay + by + cy) / 3, mz = (az + bz + cz) / 3;
  for (let i = 0; i < buildings.length; i++) {
    const k = buildings[i];
    // the triangle sits on or over the building (roof tanks, cupolas, chimneys, wall-mounted bits)
    if (my > ty + 0.9 && Math.abs(mx - k.x) < k.hx + 0.6 && Math.abs(mz - k.z) < k.hz + 0.6) return i;
  }
  return -1;
}

/**
 * Tag, add rubble and reorder. `ranges` are the triangle ranges building() wrote for each building
 * (solid and cut); everything after the building pass is claimed by position.
 */
export function layoutRuin(world: World, buildings: Box[], solid: KitGeo, cut: KitGeo, ranges: { s0: number; s1: number; c0: number; c1: number }[]): RuinLayout {
  const ty = world.town.y;
  const tagOf = (g: KitGeo, which: 's' | 'c') => {
    const n = g.triCount, tag = new Int32Array(n).fill(-1);
    ranges.forEach((r, i) => {
      const a = which === 's' ? r.s0 : r.c0, b = which === 's' ? r.s1 : r.c1;
      for (let t = a; t < b; t++) tag[t] = i;
    });
    const p = g.pos;
    for (let t = 0; t < n; t++) {
      if (tag[t] >= 0) continue;
      const o = t * 9;
      tag[t] = claim(buildings, ty, p[o], p[o + 1], p[o + 2], p[o + 3], p[o + 4], p[o + 5], p[o + 6], p[o + 7], p[o + 8]);
    }
    return tag;
  };
  const sTag = tagOf(solid, 's'), cTag = tagOf(cut, 'c');

  // rubble: one pile per building, coloured from its own walls, appended to the solid mesh
  const rub = new KitGeo();
  const rubTag: number[] = [];
  buildings.forEach((b, i) => {
    const before = rub.triCount;
    rubble(rub, b, ty, wallTint(solid, sTag, i), hash32(world.seed, i, 0x7b1e), b.z < world.town.z ? 1 : -1);
    for (let t = before; t < rub.triCount; t++) rubTag.push(i);
  });

  const nb = buildings.length;
  const out = {
    solid: [] as Run[], cut: [] as Run[], rubble: [] as Run[],
    solidBld: new Float32Array(0), cutBld: new Float32Array(0), solidIdx: new Uint32Array(0), cutIdx: new Uint32Array(0), rubbleIdx: [] as Uint32Array[],
  };
  // solid: street etc first, then each building's body followed by its rubble
  const sOrder: [KitGeo, number, number][] = []; // geo, tri, bld value
  for (let t = 0; t < sTag.length; t++) if (sTag[t] < 0) sOrder.push([solid, t, 0]);
  for (let i = 0; i < nb; i++) {
    const a = sOrder.length;
    for (let t = 0; t < sTag.length; t++) if (sTag[t] === i) sOrder.push([solid, t, i + 1]);
    out.solid.push({ start: a * 3, count: (sOrder.length - a) * 3 });
    const r = sOrder.length;
    for (let t = 0; t < rubTag.length; t++) if (rubTag[t] === i) sOrder.push([rub, t, -(i + 1)]);
    out.rubble.push({ start: r * 3, count: (sOrder.length - r) * 3 });
  }
  const cOrder: [KitGeo, number, number][] = [];
  for (let t = 0; t < cTag.length; t++) if (cTag[t] < 0) cOrder.push([cut, t, 0]);
  for (let i = 0; i < nb; i++) {
    const a = cOrder.length;
    for (let t = 0; t < cTag.length; t++) if (cTag[t] === i) cOrder.push([cut, t, i + 1]);
    out.cut.push({ start: a * 3, count: (cOrder.length - a) * 3 });
  }
  const s = rebuild(sOrder), c = rebuild(cOrder);
  copyInto(solid, s.geo); copyInto(cut, c.geo);
  out.solidBld = s.bld; out.cutBld = c.bld;
  out.solidIdx = seq(solid.triCount * 3); out.cutIdx = seq(cut.triCount * 3);
  for (const r of out.rubble) {
    out.rubbleIdx.push(out.solidIdx.slice(r.start, r.start + r.count));
    out.solidIdx.fill(0, r.start, r.start + r.count);
  }
  return out;
}

function seq(n: number) { const a = new Uint32Array(n); for (let i = 0; i < n; i++) a[i] = i; return a; }

function rebuild(order: [KitGeo, number, number][]) {
  const geo = new KitGeo();
  const bld = new Float32Array(order.length * 3);
  order.forEach(([g, t, v], k) => {
    for (let j = 0; j < 3; j++) {
      const vi = t * 3 + j;
      geo.pos.push(g.pos[vi * 3], g.pos[vi * 3 + 1], g.pos[vi * 3 + 2]);
      geo.nrm.push(g.nrm[vi * 3], g.nrm[vi * 3 + 1], g.nrm[vi * 3 + 2]);
      geo.uv.push(g.uv[vi * 2], g.uv[vi * 2 + 1]);
      geo.tile.push(g.tile[vi * 4], g.tile[vi * 4 + 1], g.tile[vi * 4 + 2], g.tile[vi * 4 + 3]);
      geo.tint.push(g.tint[vi * 4], g.tint[vi * 4 + 1], g.tint[vi * 4 + 2], g.tint[vi * 4 + 3]);
      bld[k * 3 + j] = v;
    }
  });
  return { geo, bld };
}

function copyInto(dst: KitGeo, src: KitGeo) {
  dst.pos = src.pos; dst.nrm = src.nrm; dst.uv = src.uv; dst.tile = src.tile; dst.tint = src.tint;
}

/** The average tint of a building's clapboard walls (so its rubble is its own colour). */
function wallTint(g: KitGeo, tag: Int32Array, i: number): RGB {
  let r = 0, gg = 0, b = 0, n = 0;
  for (let t = 0; t < tag.length; t++) {
    if (tag[t] !== i) continue;
    const vi = t * 3;
    const u = g.tile[vi * 4], v = g.tile[vi * 4 + 1];
    if (!WALL_TILES.some((nm) => Math.abs(u - TILES[nm][0]) + Math.abs(v - TILES[nm][1]) < 1e-4)) continue;
    r += g.tint[vi * 4]; gg += g.tint[vi * 4 + 1]; b += g.tint[vi * 4 + 2]; n++;
  }
  return n ? [r / n, gg / n, b / n] : [0.6, 0.7, 0.7];
}

/**
 * A burnt-out building: a low heap of broken boards over the footprint, a few slabs of its own
 * clapboard and roof leaning in the heap, a charred corner post or two still standing and the
 * stub of a brick chimney. Stays under BUILDING.rubbleH + ~1 m in the middle (the walkable top is
 * the sim's), with only thin posts going higher.
 */
function rubble(g: KitGeo, b: Box, ty: number, wall: RGB, seed: number, side: number) {
  const j = new Jit(seed);
  const W = b.hx * 0.92, D = b.hz * 0.92;
  const dark: RGB = [0.13, 0.11, 0.1], ash: RGB = [0.38, 0.36, 0.34];
  // the front fell out into the street: a second, lower lobe of the heap over the sim's spill
  // (sim/destruct.ts BUILDING.spillD / spillH), so the cover you hide behind is the heap you see
  const fz = b.z + side * (b.hz + BUILDING.spillD * 0.5);
  const heap = (x: number, z: number) => {
    // a soft dome: highest in the middle, feathering out past the footprint edge
    const u = (x - b.x) / (W + 0.8), v = (z - b.z) / (D + 0.8);
    const su = (x - b.x) / (b.hx * 0.95 + 0.5), sv = (z - fz) / (BUILDING.spillD * 0.5 + 0.9);
    return Math.max(Math.max(0, 1 - (u * u + v * v)) * 2.0, Math.max(0, 1 - (su ** 4 + sv * sv)) * (BUILDING.spillH + 0.35));
  };
  // where the litter lands: mostly the footprint, a good share of it the spill
  const spot = (spread: number): [number, number] => j.next() < 0.3
    ? [b.x + j.range(-b.hx, b.hx) * 0.9, fz + j.range(-1, 1) * BUILDING.spillD * 0.5 * spread]
    : [b.x + j.range(-W, W) * spread, b.z + j.range(-D, D) * spread];
  const at = (x: number, z: number, lift = 0): P3 => [x, ty + heap(x, z) * j.range(0.55, 1) + lift, z];
  // a skirt of ash and cinders round the heap
  {
    const F = new Frame(b.x, b.z, 1, 0);
    box(g, F, -W - 1.4, W + 1.4, ty - 0.08, ty + 0.06, -D - 1.4, D + 1.4, 'plain', [0.2, 0.18, 0.17]);
    box(g, F, -W - 0.4, W + 0.4, ty - 0.08, ty + 0.3, -D - 0.4, D + 0.4, 'rawwood', [0.16, 0.13, 0.11]);
    const S = new Frame(b.x, fz, 1, 0), sd = BUILDING.spillD * 0.5;
    box(g, S, -b.hx - 0.6, b.hx + 0.6, ty - 0.08, ty + 0.05, -sd - 1.2, sd + 1.2, 'plain', [0.2, 0.18, 0.17]);
    box(g, S, -b.hx * 0.85, b.hx * 0.85, ty - 0.08, ty + 0.35, -sd, sd, 'rawwood', [0.16, 0.13, 0.11]);
  }
  // boards: the bulk of the heap
  const nBoards = Math.round(W * D * 3.5) + 50;
  for (let k = 0; k < nBoards; k++) {
    const [x, z] = spot(j.range(0.4, 1));
    const a = j.range(0, Math.PI * 2), L = j.range(1.2, 3.4), tilt = j.range(-0.45, 0.45);
    const p0 = at(x, z, 0.05), p1: P3 = [x + Math.cos(a) * L, p0[1] + tilt * L * 0.5, z + Math.sin(a) * L];
    p1[1] = Math.max(ty + 0.02, Math.min(p1[1], ty + heap(p1[0], p1[2]) + 0.4));
    const char = j.next();
    beam(g, p0, p1, j.range(0.14, 0.28), j.range(0.05, 0.1), 'rawwood', char < 0.6 ? dark : char < 0.88 ? ash : [0.7, 0.6, 0.5]);
  }
  // the core: heavy charred timbers and floor sections piled in the middle, so the heap has mass
  const nCore = 8 + (j.next() * 5 | 0);
  for (let k = 0; k < nCore; k++) {
    const x = b.x + j.range(-W, W) * 0.55, z = b.z + j.range(-D, D) * 0.55;
    const a = j.range(0, Math.PI * 2), L = j.range(1.8, 3.2);
    const p0 = at(x, z, -0.25), p1: P3 = [x + Math.cos(a) * L, p0[1] + j.range(-0.5, 0.6), z + Math.sin(a) * L];
    p1[1] = Math.max(ty + 0.1, p1[1]);
    beam(g, p0, p1, j.range(0.6, 1.1), j.range(0.3, 0.5), 'rawwood', j.next() < 0.7 ? dark : ash);
  }
  // slabs of its own walls and roof, leaning in the heap
  const nSlab = 12 + (j.next() * 5 | 0);
  for (let k = 0; k < nSlab; k++) {
    const [x, z] = spot(0.7);
    const a = j.range(0, Math.PI * 2), L = j.range(1.8, 3.6), lean = j.range(0.3, 0.95);
    const p0 = at(x, z, 0.1), p1: P3 = [x + Math.cos(a) * L * Math.cos(lean), p0[1] + Math.sin(lean) * L * 0.7, z + Math.sin(a) * L * Math.cos(lean)];
    const roof = k % 3 === 2;
    beam(g, p0, p1, j.range(1.2, 2.2), 0.1, roof ? 'shingle' : (j.next() < 0.5 ? 'clap' : 'clap2'), roof ? [0.62, 0.38, 0.3] : [wall[0] * 0.8, wall[1] * 0.75, wall[2] * 0.7]);
  }
  // small stuff: splinters, shingles and broken bits strewn over and around the heap
  const nBits = Math.round(W * D * 6) + 80;
  for (let k = 0; k < nBits; k++) {
    const r = j.range(0.2, 1.25), a0 = j.range(0, Math.PI * 2);
    const x = b.x + Math.cos(a0) * (W + 1) * r, z = b.z + Math.sin(a0) * (D + 1) * r;
    const a = j.range(0, Math.PI * 2), L = j.range(0.3, 0.9);
    const p0 = at(x, z, 0.02), p1: P3 = [x + Math.cos(a) * L, p0[1] + j.range(-0.15, 0.25), z + Math.sin(a) * L];
    p1[1] = Math.max(ty + 0.02, p1[1]);
    const c = j.next();
    beam(g, p0, p1, j.range(0.08, 0.3), j.range(0.04, 0.12), c < 0.15 ? 'shingle' : 'rawwood', c < 0.6 ? dark : c < 0.85 ? ash : [0.7, 0.6, 0.5]);
  }
  // pieces that say what stood here: window frames, a door, the sign, a sheet of tin roof
  const bits: TileName[] = ['win0', 'win2', 'doorWood', j.next() < 0.5 ? 'sign1' : 'sign4', 'corrugated', 'win1'];
  for (const tile of bits) {
    const x = b.x + j.range(-W, W) * 0.8, z = b.z + j.range(-D, D) * 0.8;
    const a = j.range(0, Math.PI * 2), L = tile === 'corrugated' ? j.range(1.8, 2.6) : j.range(0.9, 1.4), lean = j.range(0.15, 0.7);
    const p0 = at(x, z, 0.1), p1: P3 = [x + Math.cos(a) * L * Math.cos(lean), p0[1] + Math.sin(lean) * L, z + Math.sin(a) * L * Math.cos(lean)];
    beam(g, p0, p1, tile === 'corrugated' ? 1.6 : j.range(0.8, 1.1), 0.08, tile, tile === 'corrugated' ? [0.55, 0.45, 0.4] : [0.6, 0.52, 0.46]);
  }
  // the back wall's last stretch still standing, tipped in over the heap
  {
    const sz = j.next() < 0.5 ? -1 : 1, x = b.x + j.range(-W, W) * 0.4, z = b.z + sz * D * 0.9;
    const h = j.range(2.4, 3.6), w = j.range(1.8, 2.8);
    beam(g, [x, ty - 0.05, z], [x + j.range(-0.4, 0.4), ty + h, z - sz * j.range(0.4, 0.9)], w, 0.12, j.next() < 0.5 ? 'clap' : 'clap2', [wall[0] * 0.6, wall[1] * 0.55, wall[2] * 0.5]);
  }
  // a charred post or two still standing at the corners, one with a crossbeam hanging off it
  const posts = 2 + (j.next() * 2 | 0);
  for (let k = 0; k < posts; k++) {
    const sx = j.next() < 0.5 ? -1 : 1, sz = j.next() < 0.5 ? -1 : 1;
    const x = b.x + sx * W * 0.95, z = b.z + sz * D * 0.95, h = j.range(2.2, 4.2);
    const top: P3 = [x + j.range(-0.25, 0.25), ty + h, z + j.range(-0.25, 0.25)];
    beam(g, [x, ty - 0.1, z], top, 0.24, 0.24, 'rawwood', dark);
    if (k === 0) beam(g, top, [top[0] - sx * j.range(1.2, 2.2), ty + heap(x, z) + 0.2, top[2] - sz * j.range(0.2, 0.8)], 0.18, 0.18, 'rawwood', dark);
  }
  // the chimney stub: brick outlasts the wood
  if (j.next() < 0.6) {
    const F = new Frame(b.x + j.range(-W, W) * 0.5, b.z + j.range(-D, D) * 0.5, 1, 0);
    box(g, F, -0.45, 0.45, ty - 0.1, ty + j.range(1.3, 2.4), -0.45, 0.45, 'brick', [0.8, 0.62, 0.55]);
  }
}
